import type {
  QualityControlFlag,
  QualityControlOutput,
  QualityControlSection,
} from '@aloft/toolkit-shared';
import {
  ALERT_TYPE_LABELS,
  QUALITY_CONTROL_SECTION_TITLES,
  getFlagFields,
  getNumberedPendingQualityControlSections,
  getQualityControlFlagTitle,
} from '@aloft/toolkit-shared';
import { Block, Button, Flex, Modal, Typography } from '@aloft/toolkit-sui';

import type { MutableRefObject, RefObject } from 'react';
import { useState } from 'react';

import { QualityControlReviewForm } from './QualityControlReviewForm.tsx';
import { QualityControlReviewFormCard } from './QualityControlReviewFormCard.tsx';
import { useResolveQualityControlFlag } from './useResolveQualityControlFlag.ts';

const photoUrlsFromFlag = (flag: QualityControlFlag): string[] =>
  'photoUrls' in flag ? flag.photoUrls : [];

const QualityControlReviewFlag = ({ flag, index }: { flag: QualityControlFlag; index: number }) => {
  const title = getQualityControlFlagTitle(flag.category);
  const photoUrls = photoUrlsFromFlag(flag);

  return (
    <Flex flexDirection="column" gap="12px" width="100%">
      <Typography variant="h4" color="buttonDefault">
        {`${index}. ${title}`}
      </Typography>
      <Flex flexDirection="column" gap="12px" paddingLeft="20px" width="100%">
        <Typography variant="body1">{flag.description}</Typography>
        {photoUrls.length > 0 && (
          <Flex gap="12px" flexWrap="wrap">
            {photoUrls.map((url, photoIndex) => (
              <Flex
                key={`${url}-${photoIndex}`}
                width="120px"
                height="120px"
                overflow="hidden"
                flexShrink={0}
              >
                <img
                  src={url}
                  alt={title}
                  width={120}
                  height={120}
                  style={{ objectFit: 'cover', display: 'block' }}
                />
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const CostToCureReviewFlags = ({
  flags,
  index,
}: {
  flags: { flag: QualityControlFlag; index: number }[];
  index: number;
}) => <CostToCureReviewFlagsContent flags={flags} index={index} />;

const CostToCureReviewFlagsContent = ({
  flags,
  index,
}: {
  flags: { flag: QualityControlFlag; index: number }[];
  index: number;
}) => {
  const previewUrls = flags.flatMap(({ flag }) => photoUrlsFromFlag(flag));
  const [previewIndex, setPreviewIndex] = useState<number>();
  const previewUrl = previewIndex === undefined ? undefined : previewUrls[previewIndex];

  return (
    <Flex flexDirection="column" gap="12px" width="100%">
      <Typography variant="h4" color="buttonDefault">
        {`${index}. Cost-to-Cure Misalignment`}
      </Typography>
      <ul
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          listStyleType: 'disc',
          margin: 0,
          paddingLeft: '20px',
        }}
      >
        {flags.map(({ flag }) => {
          const title = getQualityControlFlagTitle(flag.category);
          const photoUrls = photoUrlsFromFlag(flag);

          return (
            <li key={`${flag.category}-${flag.description}`}>
              <Flex flexDirection="column" gap="12px">
                <Typography variant="body1">
                  <strong>{title}</strong>
                </Typography>
                <Typography variant="body1">{flag.description}</Typography>
                {photoUrls.length > 0 && (
                  <Flex gap="12px" flexWrap="wrap">
                    {photoUrls.map((url, photoIndex) => (
                      <button
                        key={`${url}-${photoIndex}`}
                        type="button"
                        onClick={() => setPreviewIndex(previewUrls.indexOf(url))}
                        aria-label={`Preview ${title}`}
                        style={{ border: 0, cursor: 'pointer', padding: 0 }}
                      >
                        <img
                          src={url}
                          alt={title}
                          width={120}
                          height={120}
                          style={{ objectFit: 'cover', display: 'block' }}
                        />
                      </button>
                    ))}
                  </Flex>
                )}
              </Flex>
            </li>
          );
        })}
      </ul>
      <Modal
        isOpen={previewUrl !== undefined}
        onClose={() => setPreviewIndex(undefined)}
        title="Visual Evidence"
        size="4xl"
      >
        {previewUrl && previewIndex !== undefined && (
          <Flex flexDirection="column" gap="12px">
            <img
              src={previewUrl}
              alt={`Cost-to-cure evidence ${previewIndex + 1} of ${previewUrls.length}`}
              style={{
                display: 'block',
                maxHeight: '70vh',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
            {previewUrls.length > 1 && (
              <Flex alignItems="center" justifyContent="space-between">
                <Button
                  text="Previous"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setPreviewIndex((previewIndex - 1 + previewUrls.length) % previewUrls.length)
                  }
                />
                <Typography variant="body2">{`${previewIndex + 1} / ${previewUrls.length}`}</Typography>
                <Button
                  text="Next"
                  size="sm"
                  variant="secondary"
                  onClick={() => setPreviewIndex((previewIndex + 1) % previewUrls.length)}
                />
              </Flex>
            )}
          </Flex>
        )}
      </Modal>
    </Flex>
  );
};

const ReconciliationReviewFlags = ({
  flags,
  index,
}: {
  flags: { flag: QualityControlFlag; index: number }[];
  index: number;
}) => (
  <Flex flexDirection="column" gap="12px" width="100%">
    <Typography variant="h4" color="buttonDefault">
      {`${index}. Reconciliation Inconsistency`}
    </Typography>
    <ul
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        listStyleType: 'disc',
        margin: 0,
        paddingLeft: '20px',
      }}
    >
      {flags.map(({ flag }) => (
        <li key={`${flag.category}-${flag.description}`}>
          <Flex flexDirection="column" gap="12px">
            <Typography variant="h4">
              <strong>{getQualityControlFlagTitle(flag.category)}</strong>
            </Typography>
            <Typography variant="body1">{flag.description}</Typography>
          </Flex>
        </li>
      ))}
    </ul>
  </Flex>
);

const ReconciliationReviewForm = ({
  flags,
  index,
  onResolveFlags,
}: {
  flags: { flag: QualityControlFlag; index: number }[];
  index: number;
  onResolveFlags: (flags: QualityControlFlag[]) => void;
}) => (
  <QualityControlReviewFormCard
    title={`${index}. Reconciliation Inconsistency`}
    fieldTitles={[...new Set(flags.flatMap(({ flag }) => getFlagFields(flag)))]}
    defaultExpanded={flags.some(({ flag }) => flag.status !== 'resolved')}
    isResolved={flags.every(({ flag }) => flag.status === 'resolved')}
    onFieldBlur={() => onResolveFlags(flags.map(({ flag }) => flag))}
  />
);

const AlertsCommentaryReviewFlags = ({
  flags,
  index,
}: {
  flags: { flag: QualityControlFlag; index: number }[];
  index: number;
}) => (
  <Flex flexDirection="column" gap="12px" width="100%">
    <Typography variant="h4" color="buttonDefault">
      {`${index}. Insufficient Alert Commentary`}
    </Typography>
    <ul
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        listStyleType: 'disc',
        margin: 0,
        paddingLeft: '20px',
      }}
    >
      {flags.map(({ flag }) => (
        <li key={`${flag.category}-${flag.description}`}>
          <Flex flexDirection="column" gap="4px">
            <Typography variant="h4">
              {'alertType' in flag && flag.alertType
                ? `${ALERT_TYPE_LABELS[flag.alertType as keyof typeof ALERT_TYPE_LABELS] ?? flag.alertType} Commentary`
                : 'Alert Commentary'}
            </Typography>
            <Typography variant="body1">{flag.description}</Typography>
            {'suggestedRevision' in flag && flag.suggestedRevision && (
              <Typography variant="body2" color="textSecondary">
                Suggested revision: {flag.suggestedRevision}
              </Typography>
            )}
          </Flex>
        </li>
      ))}
    </ul>
  </Flex>
);

const AlertsCommentaryReviewForm = ({
  flags,
  index,
  onResolveFlags,
}: {
  flags: { flag: QualityControlFlag; index: number }[];
  index: number;
  onResolveFlags: (flags: QualityControlFlag[]) => void;
}) => (
  <QualityControlReviewFormCard
    title={`${index}. Insufficient Alert Commentary`}
    fieldTitles={[...new Set(flags.flatMap(({ flag }) => getFlagFields(flag)))]}
    defaultExpanded={flags.some(({ flag }) => flag.status !== 'resolved')}
    isResolved={flags.every(({ flag }) => flag.status === 'resolved')}
    onFieldBlur={() => onResolveFlags(flags.map(({ flag }) => flag))}
  />
);

const CostToCureReviewForm = ({
  flags,
  index,
  onResolveFlags,
}: {
  flags: { flag: QualityControlFlag; index: number }[];
  index: number;
  onResolveFlags: (flags: QualityControlFlag[]) => void;
}) => (
  <QualityControlReviewFormCard
    title={`${index}. Cost-to-Cure Misalignment`}
    fieldTitles={[...new Set(flags.flatMap(({ flag }) => getFlagFields(flag)))]}
    defaultExpanded={flags.some(({ flag }) => flag.status !== 'resolved')}
    isResolved={flags.every(({ flag }) => flag.status === 'resolved')}
    onFieldBlur={() => onResolveFlags(flags.map(({ flag }) => flag))}
  />
);

const CENTER_COLUMN_MAX_WIDTH = '720px';
const FORM_COLUMN_WIDTH = '560px';

export const QualityControlReviewDetails = ({
  qualityControl,
  scrollContainerRef,
  sectionRefs,
  updateQualityControl,
}: {
  qualityControl: QualityControlOutput;
  scrollContainerRef: RefObject<HTMLDivElement>;
  sectionRefs: MutableRefObject<Partial<Record<QualityControlSection, HTMLDivElement | null>>>;
  updateQualityControl: (body: QualityControlOutput) => Promise<QualityControlOutput>;
}) => {
  const numberedSections = getNumberedPendingQualityControlSections(qualityControl);
  const { resolveFlag, resolveFlags } = useResolveQualityControlFlag(
    qualityControl,
    updateQualityControl,
  );
  const displayIndices = new Map<QualityControlFlag, number>();
  let nextDisplayIndex = 1;

  for (const { flags } of numberedSections) {
    let hasCostToCureFlags = false;
    let hasReconciliationFlags = false;
    let hasAlertsCommentaryFlags = false;

    for (const { flag } of flags) {
      const isCostToCureFlag = 'photoUrls' in flag;
      const isReconciliationFlag =
        flag.section === 'reconciliation' &&
        flag.category !== 'bypassed_required_field' &&
        !isCostToCureFlag;
      const isAlertsCommentaryFlag = flag.category === 'insufficient_alert_commentary';

      if (
        (isCostToCureFlag && hasCostToCureFlags) ||
        (isReconciliationFlag && hasReconciliationFlags) ||
        (isAlertsCommentaryFlag && hasAlertsCommentaryFlags)
      ) {
        continue;
      }

      displayIndices.set(flag, nextDisplayIndex++);
      hasCostToCureFlags ||= isCostToCureFlag;
      hasReconciliationFlags ||= isReconciliationFlag;
      hasAlertsCommentaryFlags ||= isAlertsCommentaryFlag;
    }
  }

  return (
    <Flex
      ref={scrollContainerRef}
      data-testid="quality-control-review-scroll-container"
      flex={1}
      minWidth={0}
      minHeight={0}
      overflow="auto"
      backgroundColor="surface6"
      __dangerDoNotUseSx={{
        scrollBehavior: 'smooth',
        scrollSnapType: 'y proximity',
      }}
    >
      <Flex flexDirection="column" flex={1} minWidth={0} padding="24px" gap="40px">
        {numberedSections.map(({ section, flags }) => {
          const costToCureFlags = flags.filter(({ flag }) => 'photoUrls' in flag);
          const reconciliationFlags = flags.filter(
            ({ flag }) =>
              flag.section === 'reconciliation' &&
              flag.category !== 'bypassed_required_field' &&
              !('photoUrls' in flag),
          );
          const alertsCommentaryFlags = flags.filter(
            ({ flag }) => flag.category === 'insufficient_alert_commentary',
          );

          return (
            <Flex
              key={section}
              ref={(element) => {
                sectionRefs.current ||= {};
                sectionRefs.current[section] = element;
              }}
              flexDirection="column"
              gap="24px"
              width="100%"
              __dangerDoNotUseSx={{ scrollSnapAlign: 'start' }}
            >
              <Flex alignItems="flex-start" gap="0" width="100%">
                <Flex flex={1} minWidth={0} maxWidth={CENTER_COLUMN_MAX_WIDTH}>
                  <Typography variant="h1">{QUALITY_CONTROL_SECTION_TITLES[section]}</Typography>
                </Flex>
                <Block width={FORM_COLUMN_WIDTH} flexShrink={0} />
              </Flex>
              {flags.map(({ flag, index }) => {
                const displayIndex = displayIndices.get(flag)!;
                const isFirstCostToCureFlag = costToCureFlags[0]?.flag === flag;
                const isFirstReconciliationFlag = reconciliationFlags[0]?.flag === flag;
                const isFirstAlertsCommentaryFlag = alertsCommentaryFlags[0]?.flag === flag;
                const isGroupedReconciliationFlag =
                  flag.section === 'reconciliation' &&
                  flag.category !== 'bypassed_required_field' &&
                  !('photoUrls' in flag);
                const isGroupedAlertsCommentaryFlag =
                  flag.category === 'insufficient_alert_commentary';
                if (
                  ('photoUrls' in flag && !isFirstCostToCureFlag) ||
                  (isGroupedReconciliationFlag && !isFirstReconciliationFlag) ||
                  (isGroupedAlertsCommentaryFlag && !isFirstAlertsCommentaryFlag)
                ) {
                  return null;
                }

                return (
                  <Flex
                    key={`${section}-${flag.category}-${index}`}
                    alignItems="flex-start"
                    gap="0"
                  >
                    <Flex
                      flex={1}
                      minWidth={0}
                      maxWidth={CENTER_COLUMN_MAX_WIDTH}
                      paddingRight="24px"
                    >
                      {isFirstCostToCureFlag ? (
                        <CostToCureReviewFlags flags={costToCureFlags} index={displayIndex} />
                      ) : isFirstReconciliationFlag ? (
                        <ReconciliationReviewFlags
                          flags={reconciliationFlags}
                          index={displayIndex}
                        />
                      ) : isFirstAlertsCommentaryFlag ? (
                        <AlertsCommentaryReviewFlags
                          flags={alertsCommentaryFlags}
                          index={displayIndex}
                        />
                      ) : (
                        <QualityControlReviewFlag flag={flag} index={displayIndex} />
                      )}
                    </Flex>
                    <Flex
                      width={FORM_COLUMN_WIDTH}
                      paddingX="24px"
                      flexShrink={0}
                      flexDirection="column"
                      gap="24px"
                    >
                      {isFirstCostToCureFlag ? (
                        <CostToCureReviewForm
                          flags={costToCureFlags}
                          index={displayIndex}
                          onResolveFlags={(groupedFlags) => {
                            void resolveFlags(groupedFlags);
                          }}
                        />
                      ) : isFirstReconciliationFlag ? (
                        <ReconciliationReviewForm
                          flags={reconciliationFlags}
                          index={displayIndex}
                          onResolveFlags={(groupedFlags) => {
                            void resolveFlags(groupedFlags);
                          }}
                        />
                      ) : isFirstAlertsCommentaryFlag ? (
                        <AlertsCommentaryReviewForm
                          flags={alertsCommentaryFlags}
                          index={displayIndex}
                          onResolveFlags={(groupedFlags) => {
                            void resolveFlags(groupedFlags);
                          }}
                        />
                      ) : (
                        [{ flag, index }].map(({ flag, index }) => (
                          <Flex key={`${flag.category}-${index}`} width="100%">
                            <QualityControlReviewForm
                              flag={flag}
                              index={displayIndex}
                              defaultExpanded={flag.status !== 'resolved'}
                              onResolveFlag={(pendingFlag) => {
                                void resolveFlag(pendingFlag);
                              }}
                            />
                          </Flex>
                        ))
                      )}
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>
          );
        })}
        <Block height="96px" flexShrink={0} />
      </Flex>
    </Flex>
  );
};
